<template>
    <div class="p-learning-detail-cont__wrapper">
      <div class="p-learning-detail-cont">
        <div class="p-learning-detail-cont__head">
          <div class="p-learning-detail-cont__title">{{ chartMeta.title }}</div>
        </div>
        <div class="learningDetailCont__main">
          <div class="p-learning-detail-figure p-learning-detail-figure--block">
            <p class="ChartUnit">（{{ chartMeta.unit }}）</p>
            <div class="figureLine figureLine--year">
              <canvas :id="chartMeta.chartId"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script setup lang="ts">
  import {ref, onMounted, onUnmounted, computed, watch} from "vue";
  import Chart from 'chart.js/auto' // NFR-6251 4.x では “auto” 推奨（core+plugin自動登録）
  import type { ChartOptions, TooltipItem, LegendItem } from 'chart.js'

  // FIXME: 特定の親子間でしか使わないので、ひとまずここに置いておきます
  export interface ChartMeta {
    chartId: string;
    title: string;
    unit: string;
    extraTooltipLabel?: {
      title: string;
      unit: string;
    };
  }
  const props = withDefaults(
    defineProps<{
      chartMeta: ChartMeta;
      chartData: any;
      period?: number | null;
    }>(),
    {
      period: null
    }
  )
  const borderColors = [
    "#e75821",
    "#f6aa00",
    "#13a573",
    "#20aef5",
    "#376fd5",
    "#bb5aed",
    "#f5bca6",
    "#fbdd99",
    "#a1dbc7",
    "#a5defb",
    "#afc5ee",
    "#e4bdf8",
  ]
  const lineChartOptions = ref<ChartOptions<'line'>>({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 15 } },
    interaction: { mode: 'nearest', intersect: true },
    plugins: {
      title: { display: false },
      legend: {
        position: 'bottom',
        align: 'start',
        labels: {
          boxWidth: 15,
          font: { size: 13, family: 'oswald, sans-serif' },
          color: 'rgba(51, 51, 51, 1)',
          generateLabels: chart =>
            chart.data.datasets.map((ds: any, i): LegendItem => ({
              text: ds.label,
              hidden: !chart.isDatasetVisible(i),
              strokeStyle: ds.borderColor,
              fillStyle: ds.borderColor,
              lineWidth: 0,
              datasetIndex: i,
              fontColor: 'rgba(51, 51, 51, 1)',
            })),
        },
      },
      tooltip: {
        enabled: true,
        mode: 'nearest',
        backgroundColor: 'rgba(221, 221, 221, 1)',
        borderColor: 'rgba(0, 0, 0, 1)',
        borderWidth: 1,
        titleFont: { size: 17, family: 'fot-udmarugo-large-pr6n', weight: 700 },
        titleColor: 'rgba(51, 51, 51, 1)',
        bodyFont: { size: 14, family: 'fot-udmarugo-large-pr6n', weight: 700 },
        bodyColor: 'rgba(51, 51, 51, 1)',
        footerFont: { size: 14, family: 'fot-udmarugo-large-pr6n', weight: 700 },
        footerColor: 'rgba(51, 51, 51, 1)',
        footerMarginTop: 15,
        footerSpacing: 10,
        padding: 10,
        callbacks: {
          title: items => items[0]?.label ?? '',
          label: ctx => (ctx.dataset as any).label ?? '',
          afterLabel: () => [], // 後で fillLineChartOptions() で上書き
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          padding: 5,
          color: 'rgba(51, 51, 51, 1)',
          font: { size: 11, family: 'oswald, sans-serif' },
        },
      },
      y: {
        min: 0,
        display: true,
        stacked: false,
        grid: {
          color: ctx =>
            ctx.tick.value === 0 ? 'rgba(51, 51, 51, 1)' : 'rgba(221, 221, 221, 1)',
          lineWidth: ctx => (ctx.tick.value === 0 ? 2 : 1),
        },
        border: {
          display: false,
          color: "rgba(51, 51, 51, 1)",
        },
        ticks: {
          padding: 20,
          color: 'rgba(51, 51, 51, 1)',
          font: { size: 16, family: 'oswald, sans-serif' },
        },
      },
    },
  })

  // NFR-7201 reactive 化すると destroy() が proxy 経由となり animator から raw インスタンスを除去できないため、プレーンな変数で保持する
  let chart: Chart | null = null

  const lineChartData = computed(() => {
    let datasets = []
    props.chartData.datasets.map((dataset, index) => {
      let colorIndex = index % 12
      let data = dataset.data.map(el => el)
      let studyTimeOriginData = null
      // 学習時間の場合 かつ 期間が「月間」「年間」の場合、単位が「時間」となる
      if (props.chartMeta.chartId === "studied-time") {
        // 学習時間の場合に、ツールチップでは単位変更前のデータが必要なのでコピーを取っておく
        studyTimeOriginData = dataset.data.map(el => el)
        if (props.period == 2 || props.period == 3) {
          data = dataset.data.map((data, index) => {
            return data / 60
          })
        }
      }

      const chartDataset = {
        label: dataset.label,
        data: data,
        borderColor: borderColors[colorIndex],
        backgroundColor: "rgba(0,0,0,0)",
        tension: 0,
        borderWidth: 3,
        pointBackgroundColor: borderColors[colorIndex],
        pointBorderColor: "rgba(255, 255, 255, 1)",
        pointBorderWidth: 4,
        pointRadius: 8,
        clip: false,
        extraTooltipLabelData: dataset.extraTooltipLabelData ? dataset.extraTooltipLabelData : null,
        studyTimeOriginData: studyTimeOriginData
      }
      datasets.push(chartDataset)
    })
    return {
      labels: props.chartData.labels,
      datasets: datasets
    }
  })

  watch(
    () => [props.chartData, props.period],
    () => {
      chart?.destroy()
      chart = null
      const ctx = document.getElementById(props.chartMeta.chartId) as HTMLCanvasElement
      if (ctx) drawChart(ctx)
    },
    { deep: true }
  )

  onMounted(() => {
    fillLineChartOptions()
    const ctx = document.getElementById(props.chartMeta.chartId);
    drawChart(ctx)
  })

  onUnmounted(() => {
    chart?.destroy()
    chart = null
  })


  function fillLineChartOptions() {
    const tooltip = lineChartOptions.value.plugins!.tooltip as any
    tooltip.callbacks.afterLabel = (ctx: TooltipItem<'line'>): string[] => {
      const ds: any = ctx.dataset
      const idx = ctx.dataIndex

      // 基本表示: 値+単位
      let labels = [`${props.chartMeta.title}：${ctx.parsed.y}${props.chartMeta.unit}`]

      // 学習時間のツールチップ内の表示形式は「h時間m分」
      if (props.chartMeta.chartId === 'studied-time' && ds.studyTimeOriginData) {
        const min = ds.studyTimeOriginData[idx]
        labels = [`${props.chartMeta.title}：${Math.floor(min / 60)}時間${min % 60}分`]
      }

      // 追加ラベル
      if (ds.extraTooltipLabelData) {
        labels.push(
          `${props.chartMeta.extraTooltipLabel!.title}：${ds.extraTooltipLabelData[idx]}${props.chartMeta.extraTooltipLabel!.unit}`,
        )
      }
      return labels
    }
  }

  function drawChart(ctx) {
    chart = new Chart(ctx, {
        type: "line",
        data: lineChartData.value as any,
        options: lineChartOptions.value as any,
      }
    )
  }
</script>

<style lang="scss" scoped>
</style>
